/**
 * Exceptions: the audit's list, one by id, and its history.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import * as exceptionsApi from '../api/Exceptions';
import { parsed } from '../api/parse';
import {
	auditExceptionSchema,
	exceptionEventSchema,
	type ExceptionStatus,
} from '../schemas';
import { keys } from './keys';

export function useAuditExceptionsQuery(auditId: string) {
	return useQuery({
		queryKey: keys.audits.exceptions(auditId, ''),
		queryFn: async () =>
			parsed(
				z.array(auditExceptionSchema),
				await exceptionsApi.getAuditExceptions(auditId),
				`/audits/${auditId}/exceptions`,
			),
	});
}

/**
 * One exception, by id.
 *
 * Only for the gap the audit's own list cannot cover: a deep link opened
 * before that list has finished its first fetch. `enabled` is what keeps an
 * id that WILL appear in the list a moment later from firing a second,
 * redundant request for the same row.
 */
export function useExceptionQuery(id: string | null, enabled: boolean) {
	return useQuery({
		queryKey: keys.exceptions.detail(id ?? ''),
		queryFn: async () =>
			parsed(
				auditExceptionSchema,
				await exceptionsApi.getException(id as string),
				`/exceptions/${id}`,
			),
		enabled: Boolean(id) && enabled,
	});
}

export function useExceptionEventsQuery(id: string | null) {
	return useQuery({
		queryKey: keys.exceptions.events(id ?? ''),
		queryFn: async () =>
			parsed(
				z.array(exceptionEventSchema),
				await exceptionsApi.getExceptionEvents(id as string),
				`/exceptions/${id}/events`,
			),
		enabled: Boolean(id),
	});
}

export function useUpdateExceptionMutation() {
	const client = useQueryClient();
	return useMutation({
		mutationFn: async (input: {
			id: string;
			status: ExceptionStatus;
			note: string;
		}) =>
			parsed(
				auditExceptionSchema,
				await exceptionsApi.updateException(
					input.id,
					input.status,
					input.note,
				),
				`/exceptions/${input.id}`,
			),
		onSuccess: (_result, input) => {
			void client.invalidateQueries({
				queryKey: keys.exceptions.detail(input.id),
			});
			void client.invalidateQueries({
				queryKey: keys.exceptions.events(input.id),
			});
			/* The audit's own list and its open-exception count both move. */
			void client.invalidateQueries({ queryKey: keys.audits.all });
		},
	});
}
